import { Container, Row, Col, Card, Spinner } from "react-bootstrap";
// New package button
import { Button } from '@veeyaainnovatives/button';
import { useState, useEffect } from "react";
import axios from 'axios';
import { getApiUrl } from '../../config/api';
import dots from '../../assets/images/dots.png';

// Icon mapping for partner tiers
const tierIcons = {
    "Basic Partner": "fa-solid fa-chess-knight",
    "Pro Partner": "fa-solid fa-chess-queen",
    "Elite Partner": "fa-solid fa-chess-king",
};

// Color mapping for partner tiers
const tierColors = {
    "Basic Partner": "green",
    "Pro Partner": "blue",
    "Elite Partner": "red",
};

const PartnerProgram = ({choosePartnerPlan}) => {
    const [tiers, setTiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Fetch partner programs from API
    useEffect(() => {
        const fetchPartnerPrograms = async () => {
            try {
                setLoading(true);
                setError('');
                
                const response = await axios.get(getApiUrl('api/public/partner-programs'));
                
                if (response.data.success && response.data.data) {
                    // Map API response to component structure
                    const mappedTiers = response.data.data.map((program) => ({
                        id: program.id,
                        name: program.name,
                        description: program.short_desc || '',
                        icon: tierIcons[program.name] || "fa-solid fa-chess-pawn",
                        features: program.list_items || [],
                        color: tierColors[program.name] || "green",
                    }));
                    
                    // Sort by id (or keep API order)
                    mappedTiers.sort((a, b) => a.id - b.id);
                    
                    setTiers(mappedTiers);
                } else {
                    setTiers([]);
                }
            } catch (error) {
                console.error('Error fetching partner programs:', error);
                setError('Failed to load partner programs. Please try again later.');
                setTiers([]);
            } finally {
                setLoading(false);
            }
        };

        fetchPartnerPrograms();
    }, []);
    const chips = [
        { icon: "fa-tags", label: "Partner-Only Pricing" },
        { icon: "fa-chart-line", label: "Partner Dashboard" },
        { icon: "fa-headset", label: "Partner Priority Support" },
        { icon: "fa-comments", label: "Partner WhatsApp Lane" },
        { icon: "fa-palette", label: "Partner Branding Pack" },
        { icon: "fa-unlock", label: "No Contract Lock-In" }
    ];


    const steps = [
        {
            title: "White-Label Delivery",
            desc: "Everything is delivered with YOUR brand, not SDS."
        },
        {
            title: "Dedicated Team Assigned",
            desc: "1 architect, 1 drafter, 1 3D designer, 1 coordinator — team expands as you scale."
        },
        {
            title: "Guaranteed Delivery Time",
            desc: "Your deadlines become our deadlines."
        },
        {
            title: "Zero Overhead Costs",
            desc: "No office rent, no salaries, no training, no software licenses."
        },
        {
            title: "Built for High Volume Work",
            desc: "We can handle 50+ drawings per month effortlessly."
        },
        {
            title: "Perfect for NRIs, Remote Teams & Multi-City Firms",
            desc: "Supported across Chennai, Bangalore, Coimbatore, Hyderabad & abroad."
        }
    ];


    return (
        <div className="sds-partner">

            {/* Introduction */}
            <section className="intro">
                <Container>
                    <h1 className="about-us-title text-center mt-5">SDS <span className="theme-color-green"> Partner Program</span></h1>
                    <p className="tagline text-center ">
                        Your Professional Backend. Your Secret Design Studio. Your Unlimited Capacity
                    </p>

                    <p className="text-center">Built for Professionals Who Want to Scale Without Staff</p>

                    <Row className="mb-5 about-no-design">
                        <Col md={3} xs={6} className="mt-4 text-center">
                            <div className="icon-circle"><span className="fas fa-drafting-compass" /></div>
                            <p>Architects</p>
                        </Col>
                        <Col md={3} xs={6} className="mt-4 text-center">
                            <div className="icon-circle"><span className="fas fa-couch" /></div>
                            <p>Interior Designers</p>
                        </Col>
                        <Col md={3} xs={6} className="mt-4 text-center">
                            <div className="icon-circle"><span className="fas fa-hard-hat" /></div>
                            <p>Builders</p>
                        </Col>
                        <Col md={3} xs={6} className="mt-4 text-center">
                            <div className="icon-circle"><span className="fas fa-file-contract" /></div>
                            <p>Approval Agents</p>
                        </Col>
                        <Col md={3} xs={6} className="mt-4 text-center">
                            <div className="icon-circle"><span className="fas fa-toolbox" /></div>
                            <p>Contractors</p>
                        </Col>
                        <Col md={3} xs={6} className="mt-4 text-center">
                            <div className="icon-circle"><span className="fas fa-building" /></div>
                            <p>Real Estate Developers</p>
                        </Col>
                        <Col md={3} xs={6} className="mt-4 text-center">
                            <div className="icon-circle"><span className="fas fa-project-diagram" /></div>
                            <p>BIM Modelers</p>
                        </Col>
                        <Col md={3} xs={6} className="mt-4 text-center">
                            <div className="icon-circle"><span className="fas fa-user-friends" /></div>
                            <p>Freelancers & Small Offices</p>
                        </Col>
                    </Row>


                    <p>
                        SDS becomes your full-time design, drafting & technical team — without you hiring a single employee. You focus on clients. SDS handles the backend.
                    </p>
                </Container>
            </section>

            {/* Brand Promise */}
            <section className="brand-promise py-5">
                <Container>
                    <h2>Brand Promise — Power Your Practice</h2>
                    <Row className="mt-4">
                        <Col md={4} className="mt-4 mt-md-0">
                            <Card className="promise-card h-100">
                                <Card.Body>
                                    <span className="fa-solid fa-infinity"></span>
                                    <h5>Unlimited Capacity</h5>
                                    <p>Take 3 projects or 30 — we scale with you.</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4} className="mt-4 mt-md-0">
                            <Card className="promise-card h-100">
                                <Card.Body>
                                    <span className="fa-solid fa-users"></span>
                                    <h5>No Hiring, No HR, No Salaries</h5>
                                    <p>Get a premium team without the cost of a full office.</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4} className="mt-4 mt-md-0">
                            <Card className="promise-card h-100">
                                <Card.Body>
                                    <span className="fa-solid fa-person-digging"></span>
                                    <h5>Work Delivered Under Your Brand</h5>
                                    <p>Clients will never know SDS exists.</p>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Why Partner */}
            <section className="why-partner">
                <Container>

                    <section className="section mt-0">
                        <Container >
                            <div className="prices-design position-relative">

                                <img src={dots} alt="dots" className="dots-top" />
                                <img src={dots} alt="dots" className="dots-bottom" />

                                <Row>
                                    <Col xl={12}>
                                        <div className="text-center mb-4">
                                            <h2 className="prices-title">Why Partner With SDS ? </h2>

                                            <p className="prices-desc">You are one step closer to building your perfect product.</p>
                                        </div>
                                    </Col>
                                    <Col xl={12}>
                                        <Row>
                                            {steps.map((item, index) => (
                                                <Col md={4} key={index}>
                                                    <div className="price-card my-4">
                                                        <div className="plan-circle">
                                                            <span className={`fa-solid fa-${index + 1}`}></span>
                                                        </div>
                                                        <h3 className="plan-title mb-0">{item.title}</h3>
                                                        <span className="plan-note">{item.desc}</span>

                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </Col>

                                </Row>
                            </div>
                        </Container>
                    </section>

                </Container>
            </section>

            {/* Partner Benefits */}
            <section className="benefits ">
                <Container>
                    <h2>SDS Partner Benefits Package</h2>
                        <div className="chip-wrapper">
                            {chips.map((chip, index) => (
                                <div className="chip" key={index}>
                                    <span className={`fa-solid ${chip.icon}`}></span>
                                    {chip.label}
                                </div>
                            ))}
                        </div>
                </Container>
            </section>

            {/* Tiers */}
            <section className="tiers py-5">
                <Container>
                    <h2>Partner Program Tiers</h2>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" style={{ color: '#677a58' }} />
                            <p className="mt-3">Loading partner programs...</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-5">
                            <p className="text-danger">{error}</p>
                        </div>
                    ) : tiers.length === 0 ? (
                        <div className="text-center py-5">
                            <p>No partner programs available at the moment.</p>
                        </div>
                    ) : (
                        <Row className="mt-4">
                            {tiers.map((tier) => (
                                <Col md={4} key={tier.id || tier.name} className="mb-4">
                                    <Card className={`tier-card ${tier.color} h-100`}>
                                        <Card.Header className="py-3">
                                            <h5 className="theme-color-green mb-0 fw-bold">{tier.name}</h5>
                                            <p className="mb-0 text-mutted">{tier.description}</p>
                                            <span className={`${tier.icon} partner-plan-icon`} />
                                        </Card.Header>
                                        <Card.Body>
                                            {tier.features && tier.features.length > 0 ? (
                                                <ul>
                                                    {tier.features.map((f, i) => (
                                                        <li key={i}>{f}</li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="text-muted">No features listed.</p>
                                            )}
                                        </Card.Body>
                                        <Card.Footer>
                                            <Button 
                                                onClick={() => choosePartnerPlan(tier.name, tier.id)} 
                                                className="btn-block-green"
                                                backgroundColor="#677a58"
                                                color="#ffffff"
                                                hoverBackgroundColor="#5a6a4a"
                                                width="100%"
                                                borderRadius="100px"
                                            >
                                                Join {tier.name}
                                            </Button>
                                        </Card.Footer>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )}
                </Container>
            </section>
        </div>
    );
};

export default PartnerProgram;
